import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
	useSaveWishlistMutation,
	useUnSaveWishlistMutation,
	useFeaturedWishlistMutation,
	usePersonalWishlistMutation,
	usePersonalPlayListMutation
} from '../../../../service/index.js';
import api from '../../../../data/api/index.js';
import videoApi from '../../../../api/videoApi.js';

export const useWishlistHandling = (userData, resFeaturedPlayList) => {
	const [wishListArr, setWishListArr] = useState([]);
	const [savedVideos, setSavedVideos] = useState([]);
	const [refreshSavedVideos, setRefreshSavedVideos] = useState(null);

	// Mutations
	const [reqSaveWishlist] = useSaveWishlistMutation();
	const [reqUnSaveWishlist] = useUnSaveWishlistMutation();
	const [reqFeaturedWishlist] = useFeaturedWishlistMutation();
	const [reqPersonalWishlist] = usePersonalWishlistMutation();
	const [reqPersonalPlayList, resPersonalPlayList] = usePersonalPlayListMutation();

	// Initialize wishlist data
	const initializeWishlistData = () => {
		setWishListArr([]);

		// Process personal playlist data
		if (
			resPersonalPlayList?.isSuccess &&
			Array.isArray(resPersonalPlayList?.data?.existingVideos) &&
			resPersonalPlayList?.data?.existingVideos?.length > 0
		) {
			const recommendedPersonal = resPersonalPlayList.data.existingVideos
				.map(el =>
					el?.videos?.map(el2 => ({
						object_id: el._id,
						video_id: el2._id,
						wishlist: el2?.wishlist
					}))
				)
				.flat();

			setWishListArr(prevWishListArr => [...prevWishListArr, ...recommendedPersonal]);
		}

		// Process featured playlist data
		if (
			resFeaturedPlayList?.isSuccess &&
			Array.isArray(resFeaturedPlayList?.data?.existingVideos) &&
			resFeaturedPlayList?.data?.existingVideos?.length > 0
		) {
			const recommendedFeatured = resFeaturedPlayList.data.existingVideos
				.map(el =>
					el?.videos?.map(el2 => ({
						object_id: el._id,
						video_id: el2._id,
						wishlist: el2?.wishlist
					}))
				)
				.flat();

			setWishListArr(prevWishListArr => [...prevWishListArr, ...recommendedFeatured]);
		}
	};

	// Fetch saved videos
	useEffect(() => {
		if (userData.token && userData.user?.userId) {
			videoApi.getAllSavedVideoIds(userData.user.userId).then(res => {
				setSavedVideos(res.data.savedVideos);
			});
		}
	}, [userData, refreshSavedVideos]);

	// Handle wishlist toggle
	const handleWishList = (e, playlist, video) => {
		e.preventDefault();

		// Different logic based on playlist type
		if (playlist.type === 'PPL') {
			if (wishListArr?.some(el => el?.object_id === playlist?._id && el?.video_id === video?._id && !el?.wishlist)) {
				reqSaveWishlist({ id: video?._id });
				reqPersonalWishlist({
					id: playlist?._id,
					wishlistId: video?._id,
					wishlistFlag: true
				});
			} else {
				reqUnSaveWishlist({ id: video?._id });
				reqPersonalWishlist({
					id: playlist?._id,
					wishlistId: video?._id,
					wishlistFlag: false
				});
			}
		} else {
			if (wishListArr?.some(el => el?.object_id === playlist?._id && el?.video_id === video?._id && !el?.wishlist)) {
				reqSaveWishlist({ id: video?._id });
				reqFeaturedWishlist({
					id: playlist?._id,
					wishlistId: video?._id,
					wishlistFlag: true
				});
			} else {
				reqUnSaveWishlist({ id: video?._id });
				reqFeaturedWishlist({
					id: playlist?._id,
					wishlistId: video?._id,
					wishlistFlag: false
				});
			}
		}

		// Update local state
		if (wishListArr?.some(el => el?.object_id === playlist?._id && el?.video_id === video?._id)) {
			const wishListFilter = wishListArr?.map(el =>
				el?.object_id === playlist?._id && el?.video_id === video?._id
					? { ...el, wishlist: el?.wishlist ? false : true }
					: { ...el }
			);
			setWishListArr(wishListFilter);
		}
	};

	// Handle save/unsave video
	const handleSaveUnsave = async (id, video) => {
		const isLiked =
			video.videoDetails?.savedvideo ||
			video.savedvideo ||
			(savedVideos?.length > 0 && savedVideos?.includes(video?.videoDetails?._id || video?._id)) ||
			savedVideos?.includes(video?._id);

		try {
			const response = isLiked
				? await api.unsaveWishlist(id, userData.token)
				: await api.saveWishlist(id, userData.token);

			if (response.status === 200) {
				toast.success(isLiked ? 'Unsaved' : 'Saved', {
					position: 'top-center',
					duration: 3000
				});
				setRefreshSavedVideos(response.data);
			}
		} catch (error) {
			console.error('Error fetching data: ', error);
		}
	};

	return {
		wishListArr,
		savedVideos,
		handleWishList,
		handleSaveUnsave,
		initializeWishlistData
	};
};
